import { BrandPlanTactic } from 'types'
import { Checkbox } from 'components/CheckboxGroup/Checkbox'
import Styles from './CrosspostTactics.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import { ChannelIntegrationButton } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/ChannelIntegrationButton'
import { ExternalIntegration, ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { useHover } from '@react-aria/interactions'
import { PlatformIcon } from 'screens/ScheduledPosts/PlatformIcon'
import { useDisconnectWithConfirmation } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/hooks/useDisconnectWithConfirmation'
import { noop } from 'lodash'
import { useMemo } from 'react'
import { Tooltip } from 'common/Tooltip/Tooltip'
import { InformationCircleContained } from 'blaze-ui/icons/20'

interface TacticCheckboxProps {
  applyRedesign?: boolean
  provider: ExternalIntegrationEnum
  label: string
  value: BrandPlanTactic | 'stories'
  isDisabled?: boolean
  onCancel?: () => void
  tooltip?: string
}

export const TacticCheckbox = ({
  applyRedesign = false,
  provider,
  label,
  value,
  isDisabled,
  onCancel,
  tooltip,
}: TacticCheckboxProps) => {
  const isOnboardingRedesign = applyRedesign
  const { isHovered, hoverProps } = useHover({})
  const integrations = useExternalIntegrations()
  const textVariant = isOnboardingRedesign ? 'primary' : 'largeList'

  const matchingIntegrations = useMemo(() => {
    const externalIntegrations = integrations.data?.external_integrations ?? []
    return externalIntegrations.filter((integration: ExternalIntegration) => integration.provider === provider)
  }, [integrations.data?.external_integrations, provider])

  const disconnectWithConfirmation = useDisconnectWithConfirmation(matchingIntegrations, { onCancel })

  return (
    <div className={Styles.checkboxContainer} {...hoverProps}>
      <Checkbox
        value={value}
        size={20}
        color="var(--selected-fill)"
        aria-label={label}
        className={cx(Styles.checkbox, { [Styles.applyRedesign]: isOnboardingRedesign })}
        onChange={(isSelected) => {
          if (!isSelected) {
            disconnectWithConfirmation(noop)
          }
        }}
      >
        <div className={Styles.checkboxLabel}>
          <PlatformIcon size={20} platform={provider} isBrand />
          <Text variant={textVariant} color="var(--dark-80)">
            {label}
          </Text>
          {tooltip && (
            <Tooltip label={tooltip} placement="bottom">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <InformationCircleContained size={16} color="var(--dark-15)" />
              </div>
            </Tooltip>
          )}
        </div>
        {isOnboardingRedesign && (
          <div className={Styles.channelIntegrationButton}>
            <ChannelIntegrationButton
              integrations={matchingIntegrations}
              isHovered={isHovered}
              provider={provider}
              isDisabled={isDisabled}
            />
          </div>
        )}
      </Checkbox>
    </div>
  )
}
